import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle, ExternalLink, Download, ArrowUp, ChevronDown, Loader, Copy, Check } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { ThemeToggleCompact } from '@/components/theme/ThemeToggle';
import {
  getSharedRecordByToken
} from '@/integrations/sharing/publicSharingService';
import { documentService } from '@/integrations/documents/documentService';
import { cleanMarkdownText } from '@/lib/markdown-utils';
import type { SharedRecord } from '@/integrations/supabase/types';
import type { DocumentFormat, ActiveTab } from '@/components/records/types/recordHeader.types';
import { useTranslation } from 'react-i18next';

const SharedRecord: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [record, setRecord] = useState<SharedRecord | null>(null);
  const [error, setError] = useState<{ error: string; message: string; code: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [tableOfContents, setTableOfContents] = useState<{ id: string; title: string; level: number }[]>([]);
  const [copying, setCopying] = useState(false);
  const [generatingDoc, setGeneratingDoc] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>("processed");

  const { toast } = useToast();

  useEffect(() => {
    const loadSharedRecord = async () => {
      if (!token) {
        setError({
          error: 'NO_TOKEN',
          message: t('shared.error.no_token_msg'),
          code: 'NOT_FOUND'
        });
        setLoading(false);
        return;
      }

      try {
        const result = await getSharedRecordByToken(token);

        if ('error' in result) {
          setError({
            ...result,
            code: result.code || 'UNKNOWN'
          });
        } else {
          setRecord(result);
        }
      } catch (err) {
        setError({
          error: 'UNKNOWN_ERROR',
          message: t('shared.error.unknown'),
          code: 'UNKNOWN'
        });
      } finally {
        setLoading(false);
      }
    };

    loadSharedRecord();
  }, [token]);

  // Обработка прокрутки для кнопки "Наверх"
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Генерация Table of Contents из текста документа
  useEffect(() => {
    if (!record?.post_processing_text && !record?.transcript_text) return;

    const text = record.post_processing_text || record.transcript_text || '';
    const headingRegex = /^(#{1,3})\s+(.+)$/gm;
    const toc: { id: string; title: string; level: number }[] = [];
    let match;

    while ((match = headingRegex.exec(text)) !== null) {
      const level = match[1].length;
      const title = match[2].trim();
      const id = title.toLowerCase()
        .replace(/[^а-яё\w\s]/gi, '')
        .replace(/\s+/g, '-')
        .replace(/^-+|-+$/g, '');

      if (id) {
        toc.push({ id, title, level });
      }
    }

    setTableOfContents(toc);
  }, [record]);

  // Скролл к началу страницы
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Получение текущего текста для действий
  const getCurrentText = () => {
    if (!record) return "";
    return activeTab === "transcript"
      ? cleanMarkdownText(record.transcript_text)
      : cleanMarkdownText(record.post_processing_text || record.transcript_text);
  };

  // Копирование в буфер обмена
  const copyToClipboard = async () => {
    try {
      setCopying(true);
      const textToCopy = getCurrentText();
      await navigator.clipboard.writeText(textToCopy);

      toast({
        title: t('shared.toasts.copied_title'),
        description: t('shared.toasts.copied_desc'),
      });
    } catch (error) {
      console.error("Ошибка при копировании:", error);
      toast({
        title: t('shared.toasts.copy_error_title'),
        description: t('shared.toasts.copy_error_desc'),
        variant: "destructive",
      });
    } finally {
      setCopying(false);
    }
  };

  // Скачивание как TXT
  const downloadText = () => {
    try {
      if (!record) return;

      const textToDownload = getCurrentText();
      const fileName = activeTab === "transcript"
        ? `${record.name}_транскрипция.txt`
        : `${record.name}_обработанный.txt`;

      const blob = new Blob([textToDownload], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: t('shared.toasts.download_title'),
        description: t('shared.toasts.download_success'),
      });
    } catch (error) {
      console.error("Ошибка при скачивании:", error);
      toast({
        title: t('shared.toasts.download_error_title'),
        description: t('shared.toasts.download_error_desc'),
        variant: "destructive",
      });
    }
  };

  // Получение имени файла
  const getFileName = () => {
    if (!record) return "document";
    const suffix = activeTab === "transcript"
      ? t('shared.download.transcription_suffix')
      : t('shared.download.processed_suffix');
    return `${record.name}_${suffix}`;
  };

  // Скачивание документа в различных форматах
  const handleDocumentDownload = async (format: DocumentFormat) => {
    if (!record) {
      toast({
        title: t('shared.toasts.download_error_title'),
        description: t('shared.toasts.record_not_found'),
        variant: "destructive",
      });
      return;
    }

    try {
      setGeneratingDoc(true);

      const text = getCurrentText();
      const filename = getFileName();

      toast({
        title: t('shared.toasts.gen_title'),
        description: t('shared.toasts.gen_desc'),
      });

      const response = await documentService.generateDocument({
        text,
        format,
        filename,
      });

      if (response.success && response.download_url) {
        await documentService.downloadFile(response.download_url, `${filename}.${format}`);

        toast({
          title: t('shared.toasts.gen_success_title'),
          description: t('shared.toasts.gen_success_desc'),
        });
      } else {
        throw new Error(response.message || t('shared.toasts.gen_error_msg'));
      }
    } catch (error) {
      console.error("Ошибка при создании документа:", error);
      toast({
        title: t('shared.toasts.download_error_title'),
        description: error instanceof Error ? error.message : t('shared.toasts.gen_error_msg'),
        variant: "destructive",
      });
    } finally {
      setGeneratingDoc(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-3 text-lg text-foreground">{t('shared.loading')}</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="p-8">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-semibold mb-2">{t('shared.error.access_title')}</div>
                <div>{error.message}</div>
                {error.code === 'NOT_FOUND' && (
                  <div className="mt-3 text-sm">
                    {t('shared.error.possible_reasons')}
                    <ul className="list-disc list-inside mt-1">
                      <li>{t('shared.error.reason_deleted')}</li>
                      <li>{t('shared.error.reason_expired')}</li>
                      <li>{t('shared.error.reason_invalid')}</li>
                    </ul>
                  </div>
                )}
              </AlertDescription>
            </Alert>
            <div className="mt-6 text-center">
              <Button
                onClick={() => window.location.href = 'https://voice2doc.com'}
                className="bg-primary hover:bg-primary/90"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                {t('shared.buttons.to_service')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!record) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Заголовок страницы */}
      <div className="bg-card border-b shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => window.open('https://voice2doc.com', '_blank')}
              className="text-xl font-bold text-brand-500 hover:text-brand-600 transition-colors cursor-pointer"
            >
              Voice2Doc
            </button>
            <ThemeToggleCompact />
          </div>
        </div>
      </div>

      {/* Основной контент */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex gap-8 max-w-6xl mx-auto">
          {/* Table of Contents */}
          {tableOfContents.length > 0 && (
            <div className="hidden lg:block w-64 flex-shrink-0">
              <Card className="sticky top-8">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                    {t('shared.toc_title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <nav className="space-y-1">
                    {tableOfContents.map((item, index) => (
                      <a
                        key={index}
                        href={`#${item.id}`}
                        className={`block py-1 text-sm text-muted-foreground hover:text-primary transition-colors ${item.level === 1 ? 'font-medium' :
                          item.level === 2 ? 'pl-3' : 'pl-6'
                          }`}
                      >
                        {item.title}
                      </a>
                    ))}
                  </nav>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Основной контент */}
          <Card className="flex-1 shadow-lg">
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <CardTitle className="text-xl font-semibold text-foreground break-words mb-3">
                    {record.name}
                  </CardTitle>
                </div>
              </div>

              {/* Действия с записью */}
              <div className="mt-4 pt-4 border-t border-border/30 bg-gradient-to-r from-muted/20 to-transparent rounded-lg -mx-2 px-2 py-2 flex flex-wrap gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline" disabled={generatingDoc}>
                      {generatingDoc ? (
                        <Loader size={14} className="mr-1.5 animate-spin" />
                      ) : (
                        <Download size={14} className="mr-1.5" />
                      )}
                      <span className="hidden sm:inline">
                        {generatingDoc ? t('shared.buttons.download_generating') : t('shared.buttons.download')}
                      </span>
                      <ChevronDown size={12} className="ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem
                      onClick={() => handleDocumentDownload('docx')}
                      disabled={generatingDoc}
                    >
                      {t('shared.formats.docx')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleDocumentDownload('pdf')}
                      disabled={generatingDoc}
                    >
                      {t('shared.formats.pdf')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleDocumentDownload('html')}
                      disabled={generatingDoc}
                    >
                      {t('shared.formats.html')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleDocumentDownload('odt')}
                      disabled={generatingDoc}
                    >
                      {t('shared.formats.odt')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={downloadText}>
                      {t('shared.formats.txt')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={copyToClipboard}
                  disabled={copying}
                >
                  {copying ? (
                    <>
                      <Check size={14} className="mr-1.5 text-green-500" />
                      <span className="hidden sm:inline">{t('shared.buttons.copied')}</span>
                    </>
                  ) : (
                    <>
                      <Copy size={14} className="mr-1.5" />
                      <span className="hidden sm:inline">{t('shared.buttons.copy')}</span>
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>


            {/* Табы для переключения между контентом */}
            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as ActiveTab)} className="w-full">
              {/* Браузерные вкладки */}
              <div className="border-b border-border bg-gradient-to-r from-muted/30 to-muted/10 border-l-4 border-l-brand-500 border-r border-r-border/50 shadow-sm">
                <TabsList className="h-auto p-0 bg-transparent border-none ml-2">
                  <TabsTrigger
                    value="processed"
                    className="px-6 py-3 rounded-t-lg border-b-2 border-transparent data-[state=active]:border-brand-500 data-[state=active]:bg-card data-[state=active]:text-brand-600 data-[state=active]:shadow-sm hover:bg-muted/50 transition-all relative font-medium"
                  >
                    {t('shared.tabs.processed')}
                  </TabsTrigger>
                  {record.transcript_text && (
                    <TabsTrigger
                      value="transcript"
                      className="px-6 py-3 rounded-t-lg border-b-2 border-transparent data-[state=active]:border-brand-500 data-[state=active]:bg-card data-[state=active]:text-brand-600 data-[state=active]:shadow-sm hover:bg-muted/50 transition-all relative font-medium"
                    >
                      {t('shared.tabs.transcript')}
                    </TabsTrigger>
                  )}
                </TabsList>
              </div>

              {/* Контент прямо под вкладками */}
              <div className="bg-gradient-to-br from-card to-muted/5 rounded-b-lg border border-t-0 border-border/50 border-l-4 border-l-brand-500 shadow-sm">
                <TabsContent value="processed" className="m-0 p-4 sm:p-6">
                  {record.post_processing_text ? (
                    <div className="w-full max-w-none">
                      <div className="text-foreground leading-relaxed space-y-4 text-sm sm:text-base">
                        <ReactMarkdown
                          components={{
                            h1: ({ children }) => {
                              const id = typeof children === 'string'
                                ? children.toLowerCase().replace(/[^а-яё\w\s]/gi, '').replace(/\s+/g, '-').replace(/^-+|-+$/g, '')
                                : '';
                              return (
                                <h1 id={id} className="text-xl sm:text-2xl font-bold text-brand-600 mb-4 mt-6 first:mt-0 pb-2 border-b border-border">
                                  {children}
                                </h1>
                              );
                            },
                            h2: ({ children }) => {
                              const id = typeof children === 'string'
                                ? children.toLowerCase().replace(/[^а-яё\w\s]/gi, '').replace(/\s+/g, '-').replace(/^-+|-+$/g, '')
                                : '';
                              return (
                                <h2 id={id} className="text-lg sm:text-xl font-semibold text-brand-600 mb-3 mt-5 first:mt-0">{children}</h2>
                              );
                            },
                            h3: ({ children }) => {
                              const id = typeof children === 'string'
                                ? children.toLowerCase().replace(/[^а-яё\w\s]/gi, '').replace(/\s+/g, '-').replace(/^-+|-+$/g, '')
                                : '';
                              return (
                                <h3 id={id} className="text-base sm:text-lg font-medium text-brand-600 mb-2 mt-4 first:mt-0">{children}</h3>
                              );
                            },
                            p: ({ children }) => <p className="mb-3 text-foreground leading-relaxed">{children}</p>,
                            ul: ({ children }) => <ul className="mb-4 space-y-1 pl-6">{children}</ul>,
                            ol: ({ children }) => <ol className="mb-4 space-y-1 pl-6">{children}</ol>,
                            li: ({ children }) => (
                              <li className="text-foreground leading-relaxed relative">
                                <span className="absolute -left-6 text-brand-500 font-medium">•</span>
                                {children}
                              </li>
                            ),
                            strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
                            em: ({ children }) => <em className="italic text-foreground">{children}</em>,
                            blockquote: ({ children }) => (
                              <blockquote className="border-l-4 border-brand-200 pl-4 py-2 my-4 bg-brand-50/50 italic text-foreground">
                                {children}
                              </blockquote>
                            ),
                            code: ({ children }) => (
                              <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-foreground">{children}</code>
                            ),
                            pre: ({ children }) => (
                              <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm font-mono text-foreground my-4">
                                {children}
                              </pre>
                            ),
                          }}
                        >
                          {cleanMarkdownText(record.post_processing_text)}
                        </ReactMarkdown>
                      </div>
                    </div>
                  ) : (
                    <div className="text-muted-foreground italic bg-muted/30 p-4 rounded-md text-center">
                      {t('shared.empty_processed')}
                    </div>
                  )}
                </TabsContent>

                {record.transcript_text && (
                  <TabsContent value="transcript" className="m-0 p-4 sm:p-6">
                    <div className="text-foreground leading-relaxed whitespace-pre-wrap text-sm sm:text-base">
                      {cleanMarkdownText(record.transcript_text)}
                    </div>
                  </TabsContent>
                )}
              </div>
            </Tabs>
          </Card>
        </div>

        {/* Информация о сервисе */}
        <div className="mt-8 text-center">
          <div className="text-muted-foreground text-sm">
            {t('shared.footer_text')}{' '}
            <button
              onClick={() => window.open('https://voice2doc.com', '_blank')}
              className="text-brand-500 hover:text-brand-600 hover:underline cursor-pointer font-medium"
            >
              Voice2Doc
            </button>
            {' '}{t('shared.service_desc')}
          </div>
        </div>

        {/* Кнопка "Наверх" */}
        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 bg-primary hover:bg-primary/90 text-primary-foreground p-3 rounded-full shadow-lg transition-all duration-300 z-50"
            aria-label={t('shared.buttons.scroll_top_aria')}
          >
            <ArrowUp className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default SharedRecord;